import React from 'react';

import { DASH, lineAmount, money, statusChipClass, STATUS_PENDING } from './changeRequestFormat';
import FundingCell from './FundingCell';

const COLUMN_COUNT = 13;

const signedChange = (change) => {
    // Shown in whole units, so anything that rounds away moved nothing visible.
    if (Math.round(Number(change ?? 0)) === 0) {
        return $t('No change');
    }

    return Number(change) > 0 ? `+${money(change)}` : money(change);
};

/**
 * A department's or cost centre's Budget Changes log: requests awaiting or past a
 * decision beside the changes that applied with no approver. Selecting a row opens
 * the same detail pop-up as the report.
 */
export default function RecordedChangesTable({ rows, emptyText, emptyHint = null, onOpen, onDelete, deletingId }) {
    return (
        <div className="inbud-table_wrapper bc-table-wrapper">
            <table className="inbud-table bc-table">
                <thead>
                    <tr>
                        <th scope="col">{$t('Date')}</th>
                        <th scope="col">{$t('Line')}</th>
                        <th scope="col" className="bc-num">
                            {$t('From')}
                        </th>
                        <th scope="col" className="bc-num">
                            {$t('To')}
                        </th>
                        <th scope="col" className="bc-num">
                            {$t('Change')}
                        </th>
                        <th scope="col">{$t('Type')}</th>
                        <th scope="col">{$t('Category')}</th>
                        <th scope="col">{$t('Source of funds · balance impact')}</th>
                        <th scope="col">{$t('Changed by')}</th>
                        <th scope="col">{$t('Explanation')}</th>
                        <th scope="col">{$t('Approvers')}</th>
                        <th scope="col">{$t('Status')}</th>
                        <th scope="col" />
                    </tr>
                </thead>
                <tbody>
                    {rows.length === 0 && (
                        <tr>
                            <td colSpan={COLUMN_COUNT} className="tw-text-center tw-text-charcoal-50">
                                {emptyText} {emptyHint}
                            </td>
                        </tr>
                    )}

                    {rows.map((row) => {
                        const pending = row.status === STATUS_PENDING;
                        const open = () => onOpen(row);

                        return (
                            <tr
                                key={row.id}
                                className="bc-row--clickable"
                                role="button"
                                tabIndex={0}
                                aria-label={$t('View request {{reference}}', { reference: row.reference })}
                                onClick={open}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        open();
                                    }
                                }}
                            >
                                <td className="tw-whitespace-nowrap tw-tabular-nums">{row.date}</td>
                                <td>
                                    <span className="tw-font-medium">{row.line || row.description}</span>
                                    {row.line_subtitle && <span className="bc-sub">{row.line_subtitle}</span>}
                                </td>
                                <td className="bc-num tw-whitespace-nowrap">{lineAmount(row, row.original_amount)}</td>
                                <td className="bc-num tw-whitespace-nowrap">{lineAmount(row, row.new_amount)}</td>
                                <td className={`bc-num tw-whitespace-nowrap ${pending ? 'bc-stat__value--amber tw-font-medium' : ''}`}>
                                    {signedChange(row.change)}
                                </td>
                                <td>{row.type_label}</td>
                                <td>{row.category_label}</td>
                                <FundingCell funding={row.funding || {}} category={row.category} />
                                <td className="tw-whitespace-nowrap">{row.requested_by}</td>
                                <td className="tw-max-w-[220px]">{row.explanation}</td>
                                <td>
                                    {row.approvers_label ? (
                                        <span className="bc-chip bc-chip--grey tw-whitespace-nowrap">{row.approvers_label}</span>
                                    ) : row.approvers?.length > 0 ? (
                                        <span className="tw-flex tw-flex-wrap tw-gap-1">
                                            {row.approvers.map((name) => (
                                                <span key={name} className="bc-chip bc-chip--blue tw-whitespace-nowrap">
                                                    {name}
                                                </span>
                                            ))}
                                        </span>
                                    ) : (
                                        <span className="bc-dim">{DASH}</span>
                                    )}
                                </td>
                                <td>
                                    <span className={statusChipClass(row.status)}>{row.status_label}</span>
                                </td>
                                <td className="tw-text-end">
                                    {row.delete_url && pending && (
                                        // The row itself opens the pop-up, so Delete has to stop the click
                                        // reaching it.
                                        <button
                                            type="button"
                                            className="bc-del"
                                            disabled={deletingId === row.id}
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                onDelete(row);
                                            }}
                                        >
                                            {$t('Delete')}
                                        </button>
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}
